import { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IconClipboardList } from "@tabler/icons-react";
import { Heading } from "../../../../components/common/typography";
import { SearchBar } from "../../../../components/common/input";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { SimpleTable } from "../../../../components/common/simple-table";
import { EmptyState } from "../../../../components/common/empty-state";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { Pagination } from "../../../../components/common/pagination";
import { AccentStatCard } from "../../../../components/common/accent-stat-card";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useAdminChangeRequests, useAdminChangeRequestsGroupedByUser } from "../../hooks/useAdminChangeRequests";
import { useAdminChangeRequestStats } from "../../hooks/useAdminChangeRequestStats";
import { buildChangeRequestsAllColumns, buildChangeRequestsByUserColumns } from "../components/admin-change-requests-table-columns";
import type { ChangeRequestDto, DocumentsQueryDto, UserChangeRequestsDto } from "../../../../api";
import { DocumentsQueryDtoStatusEnum } from "../../../../api";

type View = "byUser" | "all";
type StatusFilter = "all" | "PENDING" | "APPROVED" | "REJECTED";

const VIEW_OPTIONS: { value: View; label: string }[] = [
  { value: "byUser", label: "By user" },
  { value: "all", label: "All" },
];

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

const VALID_STATUSES = new Set<StatusFilter>(STATUS_OPTIONS.map((o) => o.value));
const PAGE_SIZE = 10;

export function AdminChangeRequestsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<View>("byUser");

  const statusParam = searchParams.get("status") as StatusFilter | null;
  const status: StatusFilter = statusParam && VALID_STATUSES.has(statusParam) ? statusParam : "all";
  const search = searchParams.get("search") ?? "";
  const page = Number(searchParams.get("page") ?? "1") || 1;

  const setStatus = (next: StatusFilter) => {
    const p = new URLSearchParams(searchParams);
    next === "all" ? p.delete("status") : p.set("status", next);
    p.delete("page");
    setSearchParams(p);
  };

  const handleViewChange = (next: View) => {
    setView(next);
    const p = new URLSearchParams(searchParams);
    p.delete("page");
    setSearchParams(p);
  };

  const stats = useAdminChangeRequestStats();

  const baseQ: DocumentsQueryDto = {
    status: status === "all" ? undefined : (status as DocumentsQueryDtoStatusEnum),
    search: search.trim() || undefined,
    page,
    pageSize: PAGE_SIZE,
  };

  const { data: allData, isLoading: allLoading } = useAdminChangeRequests(baseQ);
  const { data: byUserData, isLoading: byUserLoading } = useAdminChangeRequestsGroupedByUser(baseQ);

  const isLoading = view === "all" ? allLoading : byUserLoading;
  const items = view === "all" ? (allData?.data ?? []) : (byUserData?.data ?? []);
  const pageCount = view === "all" ? (allData?.meta?.pageCount ?? 1) : (byUserData?.meta?.pageCount ?? 1);

  const byUserColumns = useMemo(
    () => buildChangeRequestsByUserColumns((userId) =>
      navigate(AppRoutes.ADMIN_CHANGE_REQUESTS_USER.replace(":userId", userId)),
    ),
    [navigate],
  );

  const allColumns = useMemo(
    () => buildChangeRequestsAllColumns((id) =>
      navigate(`${AppRoutes.ADMIN_CHANGE_REQUESTS}/${id}`),
    ),
    [navigate],
  );

  const paginationFooter = pageCount > 1 ? (
    <div className="flex justify-center">
      <Pagination
        page={page}
        pageCount={pageCount}
        onPageChange={(next) => {
          const p = new URLSearchParams(searchParams);
          p.set("page", String(next));
          setSearchParams(p, { replace: true });
        }}
      />
    </div>
  ) : undefined;

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-4 py-6 md:px-8 md:py-8">
      <Heading variant="h2" tone="dark">Change requests</Heading>

      <div className="grid grid-cols-3 gap-4 max-md:gap-2 max-md:[&>*]:px-3">
        <AccentStatCard
          accent="green"
          label="Approved"
          value={(stats.approved ?? 0).toLocaleString()}
        />
        <AccentStatCard
          accent="cyan"
          label="Pending approval"
          value={(stats.pending ?? 0).toLocaleString()}
        />
        <AccentStatCard
          accent="amber"
          label="Rejected"
          value={(stats.rejected ?? 0).toLocaleString()}
        />
      </div>

      <Heading variant="h3" tone="dark">
        {view === "byUser" ? "Change requests by user" : "All change requests"}
      </Heading>

      <div className="flex items-center gap-3 max-md:flex-wrap">
        <SegmentedControl
          options={VIEW_OPTIONS}
          value={view}
          onChange={handleViewChange}
          aria-label="Toggle view"
        />
        <div className="flex-1 max-md:order-last max-md:basis-full">
          <SearchBar
            value={search}
            onChange={(e) => {
              const p = new URLSearchParams(searchParams);
              e.target.value.trim() ? p.set("search", e.target.value) : p.delete("search");
              p.delete("page");
              setSearchParams(p);
            }}
            placeholder="Search"
            wrapperClassName=""
          />
        </div>
        <SegmentedControl
          options={STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
          aria-label="Filter by status"
          className="!py-1 [&_button]:!py-1"
        />
      </div>

      {isLoading ? (
        <ShimmerLoader message="Loading change requests…" lines={6} />
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-borderDefault bg-bgWhite">
          <EmptyState
            icon={IconClipboardList}
            title="No change requests"
          />
        </div>
      ) : view === "byUser" ? (
        <SimpleTable
          columns={byUserColumns}
          data={items as UserChangeRequestsDto[]}
          keyExtractor={(r) => (r as UserChangeRequestsDto).userId}
          onRowClick={(r) =>
            navigate(AppRoutes.ADMIN_CHANGE_REQUESTS_USER.replace(":userId", (r as UserChangeRequestsDto).userId))
          }
          footer={paginationFooter}
        />
      ) : (
        <SimpleTable
          columns={allColumns}
          data={items as ChangeRequestDto[]}
          keyExtractor={(r) => String((r as ChangeRequestDto).id)}
          onRowClick={(r) =>
            navigate(`${AppRoutes.ADMIN_CHANGE_REQUESTS}/${(r as ChangeRequestDto).id}`)
          }
          footer={paginationFooter}
        />
      )}
    </div>
  );
}
